/**
 * Job Store — tracks in-flight and ready async provider jobs (e.g. fal video).
 *
 * Keyed by `request_id` (the fal external job identifier) so ToolUseBlock can
 * look up push updates when a JOB_READY socket event arrives.
 *
 * On socket reconnect, seedFromSnapshot fetches GET /api/jobs to backfill any
 * JOB_READY events that fired while the socket was disconnected.
 */

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { supabase } from "@/lib/supabase/client";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

export interface JobUpdate {
  jobId: string;
  request_id: string;
  /** The job's task (session) id, from the JOB_* payload. Scopes the Studio
   *  tab's in-flight pulse to the task being viewed. */
  task_id?: string | null;
  /** The submit's block id (`jobs.block_id`), when the payload carries it. Groups
   *  in-flight overlay cards into one Studio round per media request. */
  blockId?: string | null;
  /** Present on a JOB_ERROR update — whether the job was refunded. Absent is
   *  treated as refunded (`!== false`), which drives the failed card's "No charge."
   *  line; explicit false (the unresolvable refund-0 edge) drops the promise. */
  refunded?: boolean;
  /** Present on a JOB_ERROR update — the submit's original brief, so a fully-failed
   *  round (no committed sibling) can prefill Retry with the original prompt. */
  brief?: string | null;
  /** Present on a JOB_READY update — the rehosted UniversalResult payload. */
  result?: Record<string, unknown>;
  /** Present on a JOB_ERROR update — the provider's error message (or our wrapper). */
  error?: string;
  /** Present on a JOB_PROGRESS update — the current generation phase. */
  progressPhase?: "generating";
}

interface JobStore {
  /** Map of request_id → JobUpdate for all ready jobs received this session. */
  jobUpdates: Record<string, JobUpdate>;

  /**
   * Map of jobId → JobUpdate, maintained alongside `jobUpdates`. The media
   * engine's MediaOutputBlock correlates cards by the internal job id (the fal
   * request_id isn't known to the block until the queued tool result lands).
   */
  jobUpdatesById: Record<string, JobUpdate>;

  /** Called by jobHandlers when a JOB_READY / JOB_ERROR / JOB_PROGRESS socket event arrives. */
  setJobUpdate: (update: JobUpdate) => void;

  /**
   * Fetch /api/jobs?session_id=<id>&status=ready and seed any ready jobs that
   * arrived while the socket was disconnected. Idempotent — existing entries
   * take priority (already-rendered results win over snapshot).
   */
  seedFromSnapshot: (sessionId: string) => Promise<void>;
}

async function getAuthToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

export const useJobStore = create<JobStore>()(
  devtools(
    (set, get) => ({
      jobUpdates: {},
      jobUpdatesById: {},

      setJobUpdate: (update: JobUpdate) => {
        if (!update?.request_id) return;
        set((state) => ({
          jobUpdates: { ...state.jobUpdates, [update.request_id]: update },
          ...(update.jobId
            ? {
                jobUpdatesById: {
                  ...state.jobUpdatesById,
                  [update.jobId]: update,
                },
              }
            : {}),
        }));
      },

      seedFromSnapshot: async (sessionId: string) => {
        try {
          const token = await getAuthToken();
          if (!token) return;

          const url = `${SERVER_URL}/api/jobs?session_id=${encodeURIComponent(
            sessionId
          )}&status=ready`;
          const res = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) return;

          const body = (await res.json()) as {
            jobs: Array<{
              id: string;
              external_id: string | null;
              status: string;
              output_payload: Record<string, unknown> | null;
            }>;
          };

          const seed: Record<string, JobUpdate> = {};
          const seedById: Record<string, JobUpdate> = {};
          for (const j of body.jobs ?? []) {
            if (j.external_id && j.status === "ready" && j.output_payload) {
              const update: JobUpdate = {
                jobId: j.id,
                request_id: j.external_id,
                result: j.output_payload,
              };
              seed[j.external_id] = update;
              seedById[j.id] = update;
            }
          }

          if (Object.keys(seed).length === 0) return;

          // Existing push-driven entries (from JOB_READY events) take priority.
          set((state) => ({
            jobUpdates: { ...seed, ...state.jobUpdates },
            jobUpdatesById: { ...seedById, ...state.jobUpdatesById },
          }));
        } catch {
          // Graceful — snapshot is best-effort; live push covers the main path.
        }
      },
    }),
    { name: "JobStore", enabled: process.env.NODE_ENV === "development" }
  )
);
